import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { normalizeStr, Pagination, tabStyles } from './ShowStatsTabs';
import { useIsMobile } from '../hooks/useMediaQuery';
import MobileTabSelect from './MobileTabSelect';

// Shared by MyShows.js and PublicProfile.js — the "Followers" tab, which
// itself contains two sub-tabs (Followers / Following), styled after the
// Relationships sub-tab pattern on ArtistDetail.js.

const SUB_TABS = ['Followers', 'Following'];
const PAGE_SIZE = 25;

// Mobile (2026-10-28): a single dropdown instead of a scrolling button row.
function FollowSubTabBar({ active, onChange }) {
    const isMobile = useIsMobile();
    if (isMobile) return <MobileTabSelect variant="sub" tabs={SUB_TABS} active={active} onChange={onChange} />;
    return (
        <div style={styles.subTabBar}>
            {SUB_TABS.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// One table, fed either the followers or following list — columns and
// public/private handling are identical either way.
function FollowListTable({ people }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!people || people.length === 0) {
        return <div style={tabStyles.emptyTab}>Nobody here yet.</div>;
    }

    const q = normalizeStr(search);
    const filtered = search
        ? people.filter(p => [p.display_name, p.username, p.city, p.state, p.country].some(val => val && normalizeStr(val).includes(q)))
        : people;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div>
            <input
                type="text"
                placeholder="Search by name, city, state, country..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={tabStyles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={tabStyles.emptyTab}>No one matches your search.</div>
            ) : (
                <>
                    <div style={tabStyles.tableContainer}>
                        <table style={tabStyles.table}>
                            <thead>
                                <tr>
                                    <th style={tabStyles.th}>User Name</th>
                                    <th style={tabStyles.th}>City</th>
                                    <th style={tabStyles.th}>State</th>
                                    <th style={tabStyles.th}>Country</th>
                                    <th style={tabStyles.th}>Total Shows</th>
                                    <th style={tabStyles.th}>Most Recent Show</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((p, idx) => {
                                    const name = p.display_name || p.username || 'SHARC User';
                                    return (
                                        <tr key={p.id} style={idx % 2 === 0 ? tabStyles.trEven : tabStyles.trOdd}>
                                            <td style={tabStyles.td}>
                                                {p.is_public && p.username
                                                    ? <Link to={`/u/${p.username}`} style={tabStyles.tableLink}>{name}</Link>
                                                    : name}
                                            </td>
                                            <td style={tabStyles.td}>{p.is_public ? (p.city || '—') : '—'}</td>
                                            <td style={tabStyles.td}>{p.is_public ? (p.state || '—') : '—'}</td>
                                            <td style={tabStyles.td}>{p.is_public ? (p.country || '—') : '—'}</td>
                                            <td style={tabStyles.td}>
                                                {p.is_public ? <span style={tabStyles.showCount}>{p.total_shows ?? 0}</span> : '—'}
                                            </td>
                                            <td style={{ ...tabStyles.td, whiteSpace: 'nowrap' }}>
                                                {p.is_public ? formatDate(p.most_recent_show) : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── FollowersFollowingTab ────────────────────────────────────────────────────
export function FollowersFollowingTab({ followers, following }) {
    const [activeSubTab, setActiveSubTab] = useState(0);
    return (
        <div>
            <FollowSubTabBar active={activeSubTab} onChange={setActiveSubTab} />
            <div style={styles.subTabContent}>
                {activeSubTab === 0 && <FollowListTable people={followers} />}
                {activeSubTab === 1 && <FollowListTable people={following} />}
            </div>
        </div>
    );
}

const styles = {
    // Tab bars (2026-09-23 scroll fix, same as MyShows.js): overflowX: 'auto'
    // forces overflow-y to 'auto' too, and the old negative-marginBottom tab
    // buttons overflowed the bar by 1–2px, so a mouse wheel over the bar
    // scrolled the bar itself instead of the page. Divider is now an inset
    // box-shadow (painted beneath the buttons, so the active underline covers
    // it with zero overflow) plus an explicit overflowY: 'hidden'.
    subTabBar: { display: 'flex', boxShadow: 'inset 0 -1px 0 #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },
};
